import { inject, injectable } from 'inversify'
import { makeObservable, observable, runInAction } from 'mobx'
import { Types } from '@core/ioc/types'
import { HttpGateway } from '@core/services/http.gateway'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import { z } from '@core/validation/validation.schema'
import { OperationRepository } from '@core/operations/operation.repository'
import type {
  OperationHandle,
  OperationOwner,
  OperationResult,
  ReadonlyOperationState,
} from '@core/operations/operation.types'
import {
  defineOperation,
  operationFailed,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'
import { FreshnessRepository } from '@core/infra/freshness/freshness.repository'
import { mapReadFailure } from '@core/services/gateway-failure.helpers'
import { MessagePacking, type ActionFailurePm, type ActionResponsePm } from '@core/feedback/feedback.packing'

export type AdminErrorDetailPm = {
  readonly requestId: string
  readonly message: string
  readonly stack: string | null
  readonly timestamp: string
}

export type AdminErrorListItemPm = {
  readonly requestId: string
  readonly message: string
  readonly timestamp: string
}

const AdminErrorDetailDtoSchema = z.object({
  requestId: z.string(),
  message: z.string(),
  stack: z.string().optional(),
  timestamp: z.string(),
})

const AdminErrorListDtoSchema = z.array(
  z.object({
    requestId: z.string(),
    message: z.string(),
    timestamp: z.string(),
  }),
)

/** Same shape as LoadBooksFailure — the retryability signal a read policy wants. */
export type LoadAdminErrorsFailure = ActionFailurePm & Readonly<{ retryable: boolean }>

// Same policy shape as books.load/admin.users.load — a small list GET (P-47:
// same request shape, same reasoning), unlike the lookup below, which keeps
// its search-box semantics.
export const LOAD_ADMIN_ERRORS_OPERATION = defineOperation<null, LoadAdminErrorsFailure>({
  id: operationId('admin.errors.load'),
  key: () => operationKey('admin-errors'),
  groups: () => [operationGroup('session')],
  pending: { kind: 'join' },
  safety: { kind: 'read' },
  timeout: {
    kind: 'after',
    milliseconds: 15_000,
    toFailure: () => ({ code: 'temporarily-unavailable', retryable: false }),
  },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

// Constant key + `replace`: this is a search box, not a list of independent
// lookups — a new search supersedes whatever the previous one was doing,
// same "latest query wins" semantics a real search field should have.
export const LOOKUP_ADMIN_ERROR_OPERATION = defineOperation<string, ActionFailurePm>({
  id: operationId('admin.errors.lookup'),
  key: () => operationKey('search'),
  groups: () => [],
  pending: { kind: 'replace' },
  safety: { kind: 'read' },
  timeout: { kind: 'none' },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

/**
 * Backs the admin error-lookup page (core/infra/observability/errors/) — the
 * observability capability, not "admin of X": its only current consumer
 * happens to be an admin (spec/30-structure.md). Cross-cutting, not
 * a domain feature, so it lives in core/infra/ (P-35). Talks
 * to GET /admin/errors/:requestId (backend/src/routes/admin.routes.ts), which
 * 403s for a non-admin and 404s for an unrecorded requestId — both real,
 * reachable `ActionFailurePm` codes now that this Repository exists to
 * trigger them (see feedback.packing.ts's tier table).
 */
@injectable()
export class AdminErrorLookupRepository implements UserScopedState {
  lastLookup: AdminErrorDetailPm | null = null
  recentErrorsPm: readonly AdminErrorListItemPm[] | null = null

  private readonly dataGateway: HttpGateway
  private readonly freshnessRepository: FreshnessRepository
  private readonly operationOwner: OperationOwner
  private readonly lookupOperation: OperationHandle<string, ActionFailurePm>
  private readonly loadOperation: OperationHandle<null, LoadAdminErrorsFailure>

  constructor(
    @inject(Types.IDataGateway) dataGateway: HttpGateway,
    @inject(OperationRepository) operationRepository: OperationRepository,
    @inject(FreshnessRepository) freshnessRepository: FreshnessRepository,
  ) {
    this.dataGateway = dataGateway
    this.freshnessRepository = freshnessRepository
    this.operationOwner = operationRepository.createOwner({ id: 'admin-error-lookup.repository', lifetime: 'session' })
    this.lookupOperation = this.operationOwner.register(LOOKUP_ADMIN_ERROR_OPERATION)
    this.loadOperation = this.operationOwner.register(LOAD_ADMIN_ERRORS_OPERATION)

    makeObservable(this, {
      lastLookup: observable.ref,
      recentErrorsPm: observable.ref,
    })
  }

  get lookupState(): ReadonlyOperationState<ActionFailurePm> {
    return this.lookupOperation.stateFor('search')
  }

  get isLoaded(): boolean {
    return this.recentErrorsPm !== null
  }

  // Route-entry preload target (RoutesConfig) — same policy as BooksRepository.
  loadIfNeeded = async (): Promise<void> => {
    if (this.isLoaded && this.freshnessRepository.isFresh('admin.errors')) return
    await this.loadRecent()
  }

  get loadState(): ReadonlyOperationState<LoadAdminErrorsFailure> {
    return this.loadOperation.stateFor(null)
  }

  loadRecent = async (): Promise<ActionResponsePm> => {
    const result = await this.loadOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.get('/admin/errors', { signal, idempotencyKey: null })
        if (!response.success) {
          return operationFailed(mapReadFailure(response))
        }

        const parsed = AdminErrorListDtoSchema.safeParse(response.result)
        if (!parsed.success) {
          return operationFailed({ code: 'invalid-external-data', retryable: false })
        }

        const errors = Object.freeze(
          parsed.data.map((dto): AdminErrorListItemPm =>
            Object.freeze({ requestId: dto.requestId, message: dto.message, timestamp: dto.timestamp }),
          ),
        )
        return operationSucceeded(errors)
      },
      {
        succeeded: (errors) => {
          this.recentErrorsPm = errors
          this.freshnessRepository.markLoaded('admin.errors')
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  lookupError = async (requestId: string): Promise<ActionResponsePm> => {
    const result = await this.lookupOperation.run(
      requestId,
      async ({ signal }) => {
        const response = await this.dataGateway.get(`/admin/errors/${encodeURIComponent(requestId)}`, {
          signal,
          idempotencyKey: null,
        })
        if (!response.success) {
          const pm = MessagePacking.unpackGatewayResponseToPm(response)
          if (pm.success) return operationFailed({ code: 'temporarily-unavailable' })
          return operationFailed(pm.failure)
        }

        const parsed = AdminErrorDetailDtoSchema.safeParse(response.result)
        if (!parsed.success) {
          return operationFailed({ code: 'invalid-external-data' })
        }

        const detail: AdminErrorDetailPm = Object.freeze({
          requestId: parsed.data.requestId,
          message: parsed.data.message,
          stack: parsed.data.stack ?? null,
          timestamp: parsed.data.timestamp,
        })
        return operationSucceeded(detail)
      },
      {
        succeeded: (detail) => {
          this.lastLookup = detail
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  // Server-side error detail is admin-only: the next session in this tab may
  // not be an admin, so both the list and the last-opened detail reset on
  // logout like any other user-scoped Singleton state (P-14's leak warning —
  // this class previously kept lastLookup across logins).
  resetUserScopedState = (): void => {
    this.operationOwner.resetAll({ kind: 'session-reset' })
    runInAction(() => {
      this.lastLookup = null
      this.recentErrorsPm = null
    })
  }

  private toActionResponsePm(result: OperationResult<unknown, ActionFailurePm>): ActionResponsePm {
    switch (result.kind) {
      case 'succeeded':
        return MessagePacking.succeeded()
      case 'failed':
        return { success: false, failure: result.error }
      case 'cancelled':
      case 'skipped':
        return MessagePacking.cancelled()
    }
  }
}
